/**
 * Accounting contracts, part 1 (03-modules.md §12, 04-data-model.md "accounting",
 * 05-api.md "Accounting and day close"; M3 task 5): GST invoice series and the
 * invoices issued in the settlement transaction (CLAUDE.md rule 7), and the
 * business day (open, current; closing is part 2, M5).
 *
 * Invoice numbers (CGST Rules, rule 46(b)): a consecutive serial number unique for
 * a financial year, at most 16 characters, of letters, digits, hyphen `-` and
 * slash `/`. v3 formats it as `{prefix}{FY code}/{serial}`, e.g. `IND/2627/15` for
 * prefix `IND/` in FY2026-27; the serial restarts at the series' first number every
 * financial year (April to March in the outlet's timezone).
 *
 * Money is integer paise (CLAUDE.md rule 2); rates are basis points.
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema, pageQuerySchema } from './common.js';
import { defineEvent } from './events.js';
import { uuidSchema } from './ids.js';
import { basisPointsSchema, paiseSchema } from './money.js';
import { gstinSchema } from './org.js';
import { TAX_RULE_COMPONENTS } from './pricing.js';
import {
  CASH_EVENTS,
  dayBlockerSchema,
  daySummarySchema,
  openShiftCheckSchema,
  pendingExpenseCheckSchema,
} from './cash.js';

// ---------------------------------------------------------------------------
// Invoice numbers

/** GST invoice number: at most 16 characters (rule 46(b)). */
export const INVOICE_NO_MAX_LENGTH = 16;
/** Characters a GST invoice number may use (rule 46(b)). */
export const INVOICE_NO_PATTERN = /^[A-Za-z0-9/-]{1,16}$/;
/**
 * A series prefix: up to 5 of the allowed characters, so a serial of at least
 * six digits always fits (5 + 4 FY digits + `/` + 6 = 16).
 */
export const INVOICE_PREFIX_MAX_LENGTH = 5;
export const INVOICE_PREFIX_PATTERN = /^[A-Z0-9/-]{0,5}$/;
/** `FY2026-27`: the Indian financial year (1 April 2026 to 31 March 2027). */
export const FINANCIAL_YEAR_PATTERN = /^FY(\d{4})-(\d{2})$/;

/** Is `label` a financial year `FYyyyy-yy` whose second year follows the first? */
export function isFinancialYearLabel(label: string): boolean {
  const m = FINANCIAL_YEAR_PATTERN.exec(label);
  if (!m) return false;
  const start = Number(m[1]);
  return start >= 2000 && start <= 2998 && (start + 1) % 100 === Number(m[2]);
}

export const financialYearSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(FINANCIAL_YEAR_PATTERN, { message: 'Expected a financial year as FY2026-27', abort: true })
  .refine(isFinancialYearLabel, { message: 'The second year must follow the first (FY2026-27)' });

export const invoicePrefixSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(INVOICE_PREFIX_PATTERN, {
    message: `Up to ${INVOICE_PREFIX_MAX_LENGTH} letters, digits, "-" or "/"`,
  });

/** The first serial of a financial year, or the next one (continuing a legacy series). */
export const invoiceSerialSchema = z.int().min(1).max(99_999_999_999);

// ---------------------------------------------------------------------------
// Invoice series

export const invoiceSeriesSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  fy: z.string(),
  prefix: z.string(),
  /** The serial the series started at this financial year (1, or the legacy continuation). */
  firstNo: z.int(),
  /** The serial the next invoice gets. */
  nextNo: z.int(),
  /** Invoices issued in it. */
  issuedCount: z.int(),
  /** The invoice number the next invoice gets, e.g. `IND/2627/16`. */
  nextDisplayNo: z.string(),
  /** The largest serial that still fits the 16 characters with this prefix. */
  maxNo: z.int(),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type InvoiceSeriesView = z.output<typeof invoiceSeriesSchema>;

/**
 * `POST {o}/invoice-series`: the outlet's series for a financial year, set up ahead
 * (a new year's prefix) or at cutover (`nextNo` continues the legacy numbering).
 * Without one, the first invoice of a year creates it with the latest year's prefix,
 * from 1.
 */
export const createInvoiceSeriesSchema = z.object({
  id: uuidSchema.optional(),
  fy: financialYearSchema,
  prefix: invoicePrefixSchema.optional(),
  nextNo: invoiceSerialSchema.optional(),
});

/** `PATCH {o}/invoice-series/{id}`: only before its first invoice (never skip or reuse a number). */
export const updateInvoiceSeriesSchema = z
  .object({ prefix: invoicePrefixSchema, nextNo: invoiceSerialSchema })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Send at least one field to change' });

export const invoiceSeriesParamsSchema = z.object({
  outletId: uuidSchema,
  seriesId: uuidSchema,
});

export const invoiceSeriesListSchema = z.object({ data: z.array(invoiceSeriesSchema) });

// ---------------------------------------------------------------------------
// Invoice blocks for offline terminals (M4 task 5; 02-architecture.md Offline, Q4)

/** Serials a block holds: at least one, at most this many per reservation. */
export const INVOICE_BLOCK_MAX_SIZE = 500;
export const INVOICE_BLOCK_DEFAULT_SIZE = 50;

/**
 * `POST {o}/invoice-blocks`: reserve a run of consecutive serials of the current
 * financial year's series for a terminal that may go offline. The terminal prints
 * `displayNo(n)` for serial `n` on the bills it makes offline and sends `n` with the
 * settlement; numbers it does not use are reported when the block is closed, never reused.
 */
export const reserveInvoiceBlockSchema = z.object({
  /** The block's id when the till minted it (a retry reserves once); the server makes one otherwise. */
  id: uuidSchema.optional(),
  /** The terminal the block is for; the calling terminal by default. */
  terminalId: uuidSchema.optional(),
  size: z.int().min(1).max(INVOICE_BLOCK_MAX_SIZE).optional(),
});

/** `[from, to]` serials, both included. */
export const serialRangeSchema = z.tuple([z.int(), z.int()]);

export const invoiceBlockSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  seriesId: uuidSchema,
  terminalId: uuidSchema,
  fy: z.string(),
  prefix: z.string(),
  fromNo: z.int(),
  toNo: z.int(),
  /** The invoice number of the first and last serial, e.g. `IND/2627/101` .. `IND/2627/150`. */
  fromDisplayNo: z.string(),
  toDisplayNo: z.string(),
  size: z.int(),
  /** The highest serial an invoice was issued with. */
  usedTo: z.int().nullable(),
  usedCount: z.int(),
  /** Serials of the block with no invoice (while open: still the terminal's to use). */
  unusedCount: z.int(),
  /** At close: the unused serials as ranges; null while open. */
  unused: z.array(serialRangeSchema).nullable(),
  reservedAt: isoTimestampSchema,
  closedAt: isoTimestampSchema.nullable(),
});
export type InvoiceBlockView = z.output<typeof invoiceBlockSchema>;

export const invoiceBlockListQuerySchema = z.object({
  terminalId: uuidSchema.optional(),
  /** Only open (`true`) or only closed (`false`) blocks. */
  open: z.enum(['true', 'false']).optional(),
});
export const invoiceBlockListSchema = z.object({ data: z.array(invoiceBlockSchema) });
export const invoiceBlockParamsSchema = z.object({ outletId: uuidSchema, blockId: uuidSchema });

// ---------------------------------------------------------------------------
// Invoices

/**
 * A B2B tax invoice's buyer (optional at settlement): GSTIN, legal name and address
 * (CGST rule 46(e): name, address and GSTIN of a registered recipient). The
 * place of supply of restaurant service is where it is served (the outlet's state);
 * `placeOfSupplyCode`, when sent, must be the outlet's state code.
 */
export const invoiceBuyerInputSchema = z.object({
  gstin: gstinSchema,
  name: z.string().trim().min(1).max(100),
  address: z.string().trim().min(1).max(300),
  placeOfSupplyCode: z
    .string()
    .trim()
    .regex(/^\d{2}$/, { message: 'A two-digit GST state code' })
    .optional(),
});
export type InvoiceBuyerInput = z.output<typeof invoiceBuyerInputSchema>;

const taxAmounts = {
  cgstPaise: paiseSchema,
  sgstPaise: paiseSchema,
  igstPaise: paiseSchema,
  cessPaise: paiseSchema,
};

export const invoiceLineSchema = z.object({
  lineId: uuidSchema,
  productId: uuidSchema,
  name: z.string(),
  /** HSN or SAC code of the item (menu), null when the item has none. */
  hsnSac: z.string().nullable(),
  qtyMilli: z.int(),
  /** Per unit with its modifiers (price basis, as on the bill). */
  unitPricePaise: paiseSchema,
  /** Pre-tax value before discount. */
  grossPaise: paiseSchema,
  /** Pre-tax discount share. */
  discountPaise: paiseSchema,
  taxablePaise: paiseSchema,
  ...taxAmounts,
  totalPaise: paiseSchema,
  /** CGST + SGST + IGST rate of the item (basis points), e.g. 500 for 5%. */
  gstRateBp: basisPointsSchema,
  cessRateBp: basisPointsSchema,
  complimentary: z.boolean(),
});
export type InvoiceLine = z.output<typeof invoiceLineSchema>;

export const invoiceChargeSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  kind: z.enum(['packaging', 'service', 'delivery', 'other']),
  taxable: z.boolean(),
  amountPaise: paiseSchema,
  ...taxAmounts,
  totalPaise: paiseSchema,
  gstRateBp: basisPointsSchema,
  /** SAC of the charge, when the outlet has one (none yet: null). */
  hsnSac: z.string().nullable(),
});

/** Tax by component and rate (packages/pricing `TaxSummaryRow`). */
export const invoiceTaxSchema = z.object({
  component: z.enum(TAX_RULE_COMPONENTS),
  rateBp: basisPointsSchema,
  taxablePaise: paiseSchema,
  taxPaise: paiseSchema,
});

/** Taxable value and tax per GST rate (the invoice's rate-wise summary). */
export const invoiceRateSchema = z.object({
  gstRateBp: basisPointsSchema,
  cessRateBp: basisPointsSchema,
  taxablePaise: paiseSchema,
  ...taxAmounts,
});

export const invoiceTotalsSchema = z.object({
  /** Pre-tax value of the items (complimentary excluded). */
  subtotalPaise: paiseSchema,
  /** Pre-tax discount. */
  discountPaise: paiseSchema,
  /** Discounts as the guest sees them (price basis). */
  discountAppliedPaise: paiseSchema,
  chargesPaise: paiseSchema,
  taxablePaise: paiseSchema,
  ...taxAmounts,
  taxPaise: paiseSchema,
  roundOffPaise: paiseSchema,
  totalPaise: paiseSchema,
});

export const INVOICE_KINDS = ['b2c', 'b2b'] as const;

const placeOfSupplySchema = z.object({ state: z.string().nullable(), code: z.string().nullable() });

export const invoiceSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  orderId: uuidSchema,
  orderNo: z.int(),
  seriesId: uuidSchema,
  fy: z.string(),
  /** The serial within the financial year. */
  number: z.int(),
  /** The GST invoice number printed on the bill. */
  displayNo: z.string(),
  kind: z.enum(INVOICE_KINDS),
  /** Calendar date of issue in the outlet's timezone (the invoice date). */
  invoiceDate: businessDateSchema,
  /** The outlet's business day it was issued in. */
  businessDate: businessDateSchema,
  issuedAt: isoTimestampSchema,
  issuedBy: uuidSchema.nullable(),
  terminalId: uuidSchema.nullable(),
  seller: z.object({
    name: z.string(),
    gstin: z.string().nullable(),
    stateCode: z.string().nullable(),
    addressLines: z.array(z.string()),
  }),
  placeOfSupply: placeOfSupplySchema,
  buyer: z
    .object({
      gstin: z.string(),
      name: z.string(),
      address: z.string(),
      stateCode: z.string(),
    })
    .nullable(),
  lines: z.array(invoiceLineSchema),
  charges: z.array(invoiceChargeSchema),
  taxes: z.array(invoiceTaxSchema),
  rates: z.array(invoiceRateSchema),
  totals: invoiceTotalsSchema,
  pricingVersion: z.string(),
});
export type InvoiceView = z.output<typeof invoiceSchema>;

export const invoiceSummarySchema = z.object({
  id: uuidSchema,
  orderId: uuidSchema,
  orderNo: z.int(),
  fy: z.string(),
  number: z.int(),
  displayNo: z.string(),
  kind: z.enum(INVOICE_KINDS),
  invoiceDate: businessDateSchema,
  businessDate: businessDateSchema,
  issuedAt: isoTimestampSchema,
  buyerGstin: z.string().nullable(),
  taxablePaise: paiseSchema,
  taxPaise: paiseSchema,
  totalPaise: paiseSchema,
});

export const invoiceListSchema = z.object({
  data: z.array(invoiceSummarySchema),
  nextCursor: z.string().nullable(),
});

/**
 * `GET {o}/invoices`: newest first by financial year and serial (the issue order; ids
 * are made before the number is taken, so they do not follow it); filter by business
 * date, financial year or number. `cursor` is the `nextCursor` of the page before.
 */
export const invoiceListQuerySchema = pageQuerySchema.extend({
  businessDate: businessDateSchema.optional(),
  fy: financialYearSchema.optional(),
  kind: z.enum(INVOICE_KINDS).optional(),
  /** The printed invoice number (exact, any case). */
  displayNo: z.string().trim().min(1).max(INVOICE_NO_MAX_LENGTH).optional(),
});

export const invoiceParamsSchema = z.object({ outletId: uuidSchema, invoiceId: uuidSchema });

// ---------------------------------------------------------------------------
// Business day

/**
 * How the outlet's business day ends (outlet setting `day.close_mode`):
 * - `clock` (default until day close ships, M5): the business date follows the
 *   clock (timezone and day-close time); the first order of a new business date
 *   opens its day and closes the previous one automatically.
 * - `manual` (06-flows.md F9): a day stays open until the manager closes it
 *   (`closeDay`, M5); orders after the day-close time still belong to it. The
 *   first order after a close opens the next day.
 */
export const DAY_CLOSE_MODES = ['clock', 'manual'] as const;
export type DayCloseMode = (typeof DAY_CLOSE_MODES)[number];
/**
 * The modes an outlet may choose. `manual` ends a day with `closeDay` (accounting part 2,
 * M5 task 8); both are selectable now.
 */
export const SELECTABLE_DAY_CLOSE_MODES: readonly DayCloseMode[] = ['clock', 'manual'];

export const DAY_OPEN_KINDS = ['manual', 'auto'] as const;
export const DAY_CLOSE_KINDS = ['manual', 'auto'] as const;

export const businessDaySchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  businessDate: businessDateSchema,
  status: z.enum(['open', 'closed']),
  openedAt: isoTimestampSchema,
  openedBy: uuidSchema.nullable(),
  /** `auto`: opened by the first order or KOT of the day. */
  openKind: z.enum(DAY_OPEN_KINDS),
  closedAt: isoTimestampSchema.nullable(),
  closedBy: uuidSchema.nullable(),
  /** `auto`: closed when the clock moved to the next business date (`clock` mode). */
  closeKind: z.enum(DAY_CLOSE_KINDS).nullable(),
  version: z.int(),
});
export type BusinessDayView = z.output<typeof businessDaySchema>;

/** `GET {o}/days/current`. */
export const currentBusinessDaySchema = z.object({
  /** The open day, null when none is open (the next order opens one). */
  day: businessDaySchema.nullable(),
  mode: z.enum(DAY_CLOSE_MODES),
  /** The business date the clock gives now (timezone and day-close time). */
  clockBusinessDate: businessDateSchema,
});

/** `POST {o}/days/open`. */
export const openDaySchema = z.object({});

export const dayParamsSchema = z.object({ outletId: uuidSchema, businessDate: businessDateSchema });

/** What keeps a day from closing (06-flows.md F9 step 6; `closeDay` is `POST {o}/days/close`). */
export const dayCloseChecksSchema = z.object({
  /** Orders of the day still running or awaiting payment (draft … partially paid). */
  unsettledOrders: z.array(
    z.object({
      orderId: uuidSchema,
      orderNo: z.int(),
      status: z.string(),
      totalPaise: paiseSchema,
      paidPaise: paiseSchema,
    }),
  ),
  /** KOTs of the day still waiting for their printer's confirmation. */
  unconfirmedKots: z.array(
    z.object({
      kotId: uuidSchema,
      kotNo: z.int(),
      orderId: uuidSchema,
      orderNo: z.int(),
      stationId: uuidSchema,
      createdAt: isoTimestampSchema,
    }),
  ),
  /** Shifts of the day not closed yet (each cashier counts the drawer first). */
  openShifts: z.array(openShiftCheckSchema),
  /** Expenses above the approval limit nobody has decided. */
  pendingExpenses: z.array(pendingExpenseCheckSchema),
  /**
   * Liquor items of a bar outlet (the `bar` feature on, `bar.closing_required` on) with no
   * closing count for the day yet (06-flows.md F9 step 4). Empty otherwise.
   */
  barClosings: z.array(z.object({ stockItemId: uuidSchema, itemName: z.string() })),
});

/** `GET {o}/days/{date}`: the day, its invoices and what blocks closing it. */
export const dayDetailSchema = businessDaySchema.extend({
  invoices: z.object({
    count: z.int(),
    totalPaise: paiseSchema,
    taxPaise: paiseSchema,
    /**
     * The numbers issued, per series (by serial, not by id or time): one per financial
     * year, so a day that runs past midnight on 31 March has two.
     */
    ranges: z.array(
      z.object({
        seriesId: uuidSchema,
        fy: z.string(),
        count: z.int(),
        firstNo: z.int(),
        lastNo: z.int(),
        firstDisplayNo: z.string(),
        lastDisplayNo: z.string(),
      }),
    ),
  }),
  checks: dayCloseChecksSchema,
  /** The blockers standing now (empty on a closed day), by kind with their count. */
  blockers: z.array(dayBlockerSchema),
  /** The day is open and nothing blocks it. */
  canClose: z.boolean(),
  /** The figures frozen when the day was closed by hand (null otherwise). */
  summary: daySummarySchema.nullable(),
});

// ---------------------------------------------------------------------------
// Events (03-modules.md §12 lists `invoice.issued` and `day.closed`; `day.opened`
// and `invoice_series.changed` are new, see the accounting README)

export const invoiceIssuedV1 = defineEvent(
  'invoice.issued',
  1,
  z.object({
    invoiceId: uuidSchema,
    orderId: uuidSchema,
    orderNo: z.int(),
    seriesId: uuidSchema,
    fy: z.string(),
    number: z.int(),
    displayNo: z.string(),
    kind: z.enum(INVOICE_KINDS),
    invoiceDate: businessDateSchema,
    businessDate: businessDateSchema,
    taxablePaise: paiseSchema,
    taxPaise: paiseSchema,
    totalPaise: paiseSchema,
  }),
);

export const invoiceSeriesChangedV1 = defineEvent(
  'invoice_series.changed',
  1,
  z.object({
    seriesId: uuidSchema,
    fy: z.string(),
    prefix: z.string(),
    nextNo: z.int(),
    created: z.boolean(),
    fields: z.array(z.string()),
  }),
);

export const invoiceBlockReservedV1 = defineEvent(
  'invoice.block_reserved',
  1,
  z.object({
    blockId: uuidSchema,
    seriesId: uuidSchema,
    fy: z.string(),
    terminalId: uuidSchema,
    fromNo: z.int(),
    toNo: z.int(),
  }),
);

/** A block was returned: its unused serials are reported here and never issued. */
export const invoiceBlockClosedV1 = defineEvent(
  'invoice.block_closed',
  1,
  z.object({
    blockId: uuidSchema,
    seriesId: uuidSchema,
    fy: z.string(),
    terminalId: uuidSchema,
    fromNo: z.int(),
    toNo: z.int(),
    usedCount: z.int(),
    unused: z.array(serialRangeSchema),
  }),
);

export const dayOpenedV1 = defineEvent(
  'day.opened',
  1,
  z.object({
    businessDayId: uuidSchema,
    businessDate: businessDateSchema,
    openKind: z.enum(DAY_OPEN_KINDS),
    /** The day closed automatically to open this one (`clock` mode). */
    previousBusinessDate: businessDateSchema.nullable(),
  }),
);

export const dayClosedV1 = defineEvent(
  'day.closed',
  1,
  z.object({
    businessDayId: uuidSchema,
    businessDate: businessDateSchema,
    closeKind: z.enum(DAY_CLOSE_KINDS),
    /**
     * A manual close over open orders or unconfirmed KOTs (`day.close_force`). Orders
     * still open then are settled later and invoiced on this closed date; consumers
     * (report aggregates) must not treat an `auto` close as final, nor a forced one.
     */
    forced: z.boolean().optional(),
  }),
);

export const ACCOUNTING_EVENTS = [
  invoiceIssuedV1,
  invoiceSeriesChangedV1,
  invoiceBlockReservedV1,
  invoiceBlockClosedV1,
  dayOpenedV1,
  dayClosedV1,
  ...CASH_EVENTS,
] as const;
